import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Listing } from 'models/Listing';
import { ASKING_PRICE_SELECT, AskingPriceSelect } from 'models/DealRoom';
import { formatPercentage, transformPriceNumberToCurrencyString } from 'utils';
import { useTranslation } from 'react-i18next';

import './SellerInfo.styles.scss';

type SellerInfoProps = {
  className?: string;
  priceGuide: AskingPriceSelect;
  amount: number | null;
  amountFrom: number | null;
  amountTo: number | null;
  listing: Listing | Partial<Listing>;
  franchisorFeePercentage: number;
  numberOfLeads: number;
  numberOfFiles: number;
};

const SellerInfo: React.FC<SellerInfoProps> = (props) => {
  const {
    className,
    franchisorFeePercentage,
    amount,
    amountFrom,
    amountTo,
    priceGuide,
    numberOfFiles,
    numberOfLeads,
  } = props;

  const RenderPriceGuide = useMemo(() => {
    const isFixedPrice = priceGuide === ASKING_PRICE_SELECT.FIXED_PRICE;
    const hasFixedPrice = isFixedPrice && !!amount;
    const hasRangePrice =
      !isFixedPrice && !!(amountFrom || amountFrom === 0) && !!amountTo;

    if (!hasFixedPrice && !hasRangePrice) return null;

    return isFixedPrice
      ? transformPriceNumberToCurrencyString(amount)
      : `${transformPriceNumberToCurrencyString(
          amountFrom,
        )} - ${transformPriceNumberToCurrencyString(amountTo)}`;
  }, [amount, amountFrom, amountTo, priceGuide]);

  const RenderRevenueShare = useMemo(() => {
    if (!franchisorFeePercentage) return null;

    return formatPercentage(franchisorFeePercentage);
  }, [franchisorFeePercentage]);

  const classes = classNames('seller-info', className);

  const { t } = useTranslation();

  return (
    <div className={classes}>
      {!!numberOfLeads && (
        <div className="seller-info__item">
          <span>{numberOfLeads}</span>
          <span>{t('leads')}</span>
        </div>
      )}

      {!!numberOfFiles && (
        <div className="seller-info__item">
          <span>{numberOfFiles}</span>
          <span>{t('files')}</span>
        </div>
      )}

      {RenderPriceGuide && (
        <div className="seller-info__item">
          <span>{RenderPriceGuide}</span>
          <span>{t('priceGuide')}</span>
        </div>
      )}

      {RenderRevenueShare && (
        <div className="seller-info__item">
          <span>{RenderRevenueShare}</span>
          <span>{t('commision')}</span>
        </div>
      )}
    </div>
  );
};

export default SellerInfo;
